<template>
  <AppDialog
    :is-open="isDialogOpened"
    size="xl"
    :ok-text="$t('common.confirm')"
    :retain-focus="false"
    :ok-loading="postRoomProcessing"
    :ok-disabled="postRoomProcessing"
    :cancel-disabled="postRoomProcessing"
    @ok="preparePostRoom"
    @cancel="closeDialog"
  >
    <template #title>
      <span class="text--large text--extra-bold">{{ $t('projects.dialogs.ProjectsNewSubscriptionDialog.projectCreation') }}</span>
    </template>
    <template #body>
      <form novalidate @keydown.enter="preparePostRoom">
        <v-row>
          <v-col>
            {{$t('projects.dialogs.ProjectsNewSubscriptionDialog.optionsEditableLater')}}
          </v-col>
        </v-row>
        <v-divider class="mt-4 mb-6"/>
        <v-row>
          <v-col>
            {{ $t('projects.dialogs.ProjectsNewSubscriptionDialog.text') }}
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="pb-0">
            <AppTextField v-model.trim="room.name"
                          class="required"
                          :label="$t('projects.dialogs.ProjectsNewSubscriptionDialog.projectName')"
                          :error-messages="roomNameError"
                          maxLength="100"
                          counter="100"
            />
          </v-col>
        </v-row>
        <v-row>
          <ProjectsMetadataSelectGroup :room="room"/>

          <v-col cols="12" md="6" class="mb-2">
            <template v-if="subscriptionLabelsPending">
              <v-skeleton-loader type="text"/>
            </template>
            <template v-else>
              <AppSelect v-if="computedRoomsLabels"
                v-model="room.labelId"
                :items="computedRoomsLabels"
                :label="$t('projects.dialogs.ProjectsNewSubscriptionDialog.status')"
                item-title="text"
                item-value="id"
              >
              <template #item="{ item, props }">
                <v-list-item v-bind="props">
                  <template #title>
                    <div v-if="item.raw.id === null">
                      {{ item.raw.text }}
                    </div>
                    <AppProjectLabel v-else :label="item.raw.label"/>
                  </template>
                </v-list-item>
              </template>
              <template #selection="{ item }">
                <div v-if="item.raw.id === null">
                  {{ item.raw.text }}
                </div>
                <AppProjectLabel v-else :label="item.raw.label"/>
              </template>
            </AppSelect>
          </template>
        </v-col>
        <v-col cols="12" md="6">
          <AppDatePicker ref="picker"
                         v-model="room.expClosingDate"
                         :label="$t('projects.dialogs.ProjectsNewSubscriptionDialog.plannedCompletionDate')"
                         class="w-100"
                         min="2000-01-01"
          />
        </v-col>
        <v-col cols="12" md="6" class="pb-0">
          <AppTextField v-model.trim="room.cmRef"
                        :label="$t('projects.dialogs.ProjectsNewSubscriptionDialog.customerRef')"
                        maxLength="100"
                        counter="100"
          />
        </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-switch v-model="room.optHideGroups"
                      density="comfortable"
                      hide-details
                      :label="$t('projects.dialogs.ProjectsNewSubscriptionDialog.hideGroups')"
                      color="success"
            />
            <v-switch v-model="room.optAllowGuestBibles"
                      density="comfortable"
                      hide-details
                      :label="$t('projects.dialogs.ProjectsNewSubscriptionDialog.optAllowGuestBibles')"
                      color="success"
            />
          </v-col>
        </v-row>
      </form>
    </template>
  </AppDialog>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { maxLength, required } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDatePicker from '@/common/AppDatePicker.vue'
import AppProjectLabel from '@/common/AppProjectLabel.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppDialog from '@/common/dialogs/AppDialog.vue'
import { formFieldErrors } from '@/common/utils/form'
import { ROOM_TYPES } from '@/common/utils/roomTypes'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { RoomToPost } from '@/models/room.model'
import { GET_SUBSCRIPTION_LABELS } from '@/store/modules/rooms/action_types'

import ProjectsMetadataSelectGroup from './ProjectsMetadataSelectGroup.vue'

export default {
  name: 'ProjectsNewSubscriptionDialog',
  components: { AppDialog, AppTextField, AppDatePicker, AppSelect, AppProjectLabel, ProjectsMetadataSelectGroup },
  mixins: [dialogMixin],
  setup: () => ({ v$: useVuelidate() }),
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  validations: {
    room: {
      name: {
        required,
        maxLength: maxLength(100),
      },
    },
  },
  data () {
    return {
      room: new RoomToPost(),
    }
  },
  computed: {
    ...mapState('room', ['postRoomProcessing']),
    ...mapState('rooms', ['subscriptionLabels', 'subscriptionLabelsPending']),
    ...mapGetters('user', ['roomTypes']),
    computedRoomsLabels () {
      const labels = []
      labels.push({
        id: null,
        text: this.$t('projects.dialogs.ProjectsNewSubscriptionDialog.noLabel'),
      })
      if (this.subscriptionLabels) {
        labels.push(
          ...this.subscriptionLabels.map(
            subscriptionLabel => ({
              id: subscriptionLabel.id,
              text: subscriptionLabel.label[this.$i18n.locale],
              label: {
                label: subscriptionLabel.label,
                color: subscriptionLabel.color,
              },
            }),
          ),
        )
      }
      return labels
    },
    roomNameError () {
      return formFieldErrors(
          this.v$.room.name,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('projects.dialogs.ProjectsNewSubscriptionDialog.projectName') })],
            ['maxLength', this.$t('common.validations.maxLength', { fieldName: this.$t('projects.dialogs.ProjectsNewSubscriptionDialog.projectName') })],
          ],
      )

    },
  },
  async mounted () {
    try {
      await this.GET_SUBSCRIPTION_LABELS(
        {
          params: {
            isDataroom: false,
          },
        },
      )
    } catch (e) {
      toast.error(this.$t('projects.dialogs.ProjectsNewSubscriptionDialog.getRoomsLabelsError'))
    }
  },
  methods: {
    ...mapActions('rooms', [GET_SUBSCRIPTION_LABELS]),
    ...mapActions('room', ['postRoom']),
    async preparePostRoom () {
      this.v$.$touch()
      if (!this.v$.$invalid) {
        this.room.type = this.roomTypes.find((roomType) => roomType.type === ROOM_TYPES.PROJECT_UNLIMITED).id
        try {
          await this.postRoom({ room : this.room })
          toast.success(this.$t('projects.dialogs.ProjectsNewSubscriptionDialog.postRoomSuccess'))
        } catch (error) {
          console.error(error)
          toast.error(this.$t('projects.dialogs.ProjectsNewSubscriptionDialog.postRoomError'))
        }
      }
    },
  },
}
</script>
